import { roleColor, roleLabel, roleLabelKey, roleOf, allRoles } from '../functions/index.js'
import { personTeams } from '@symbo-ls/workspace-shared/functions/teams.js'

// UUID and Mongo-ObjectId detection — protects every PersonCard text
// factory from rendering raw IDs when `s.name` ends up unexpectedly
// holding a foreign-key value (incomplete row hydration, schema drift).
const _idLike = (v) =>
  typeof v === 'string' &&
  (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v) ||
    /^[0-9a-f]{24}$/i.test(v))

const _personLabel = (s) => {
  const name = s?.name
  if (name && !_idLike(name)) return name
  const username = s?.username
  if (username && !_idLike(username)) return username
  const email = s?.email
  if (email && typeof email === 'string') return email.split('@')[0]
  return null
}

// Per-user role assignments live in `s.root.roles.byUser` (the matching
// OrgMember._id per user lives in `s.root.roles.byUserMemberId`), both
// loaded from GET /organizations/:orgId/members on sign-in — CU-10 §90 /
// Supabase-retirement sweep. All access is funneled through the helpers in
// functions/index.js (roleOf / memberIdOf / roleColor / roleLabel) so the
// underlying shape can be reshaped without touching components.

// Child keys are prefixed `Pc` so they don't collide with framework
// component names (Avatar, Tag, Title) which would otherwise be auto-
// extended by the global registry and silently break the render.
export const PersonCard = {
  extends: 'Link',
  href: (el, s) => `/profile?u=${encodeURIComponent(s.email || '')}`,
  flow: 'y',
  gap: 'B',
  padding: 'B',
  round: 'radiusSheet',
  // A card inside a sheet is the brand `card` (DESIGN_LANGUAGE §2.1): one
  // step off the pane. `common-box` IS the pane's own ground (dark gray2,
  // light white) and read 1.00:1 live (dev /team-members, 2026-10-02); the
  // `overlay` fill before it did the same. Hover still lifts with a shadow.
  // (__tests__/personCardSurfaceStep.test.js)
  theme: 'card',
  position: 'relative',
  overflow: 'hidden',
  // Browser-native viewport culling — /people PgGrid mounts every member card
  // unwindowed; `auto` skips painting off-screen cards and `contain-intrinsic-size`
  // holds a stable slot (last-known size, 160px fallback) so the grid + scrollbar
  // don't jump as cards scroll into view.
  contentVisibility: 'auto',
  containIntrinsicSize: 'auto 160px',
  cursor: 'pointer',
  textDecoration: 'none',
  color: 'title',
  ':hover': { shadow: 'float' },

  PcHeader: {
    flow: 'x',
    align: 'center',
    gap: 'A',
    marginTop: 'Z',

    // Built-in `Avatar` is `tag: 'img'` — void element, no children
    // allowed (PcOnlineDot can't nest), no `text` fallback. We need a
    // div-based avatar that hosts both the initials fallback AND the
    // online-dot overlay. Renders an inner <img> only when `s.avatar`
    // is a real URL (starts with http or /), otherwise falls back to
    // initials text on the colored background. This is the same shape
    // PersonPopoverCard's avatar uses.
    // House identity plate (§4.3 — permanent plates keep overlayStrong with
    // neutral ink; the WhCodeChip/ComAvatar/PicAvatar carve-out). The old
    // per-email hue plate stamped white ink on 7 brand hues — 5 of 7 failed
    // AA (1.55:1 on indigo, 2.18:1 on cerulean, measured 2026-08-08), and a
    // rainbow roster grid fights §4.4's one-hue law. colorFromEmail retired.
    // `theme: 'field'` at `glyphPlate`: the `overlayStrong` plate measured
    // 1.00:1 on the card (the initials floated), dev 2026-10-01.
    PcAvatar: {
      align: 'center center',
      boxSize: 'glyphPlate',
      minWidth: 'glyphPlate',
      round: '50%',
      color: 'title',
      fontSize: 'B',
      fontWeight: '700',
      position: 'relative',
      overflow: 'hidden',
      theme: 'field',

      // Image layer — MOUNTED only when `s.avatar` is a URL-like string:
      // `if:`, never `hide:` (display:none leaves the <img> fetching its
      // src, so non-URL values 404ed against the origin — fixed 2026-09-01,
      // same class as the IntranetRows plate). Guarded src as the belt.
      AvatarImg: {
        tag: 'img',
        if: (el, s) => {
          const v = s?.avatar
          return typeof v === 'string' && /^(https?:|\/)/.test(v)
        },
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        src: (el, s) => {
          const v = s?.avatar
          return typeof v === 'string' && /^(https?:|\/)/.test(v) ? v : null
        },
        alt: (el, s) => _personLabel(s) || el.call('polyglot', 'workspace.person.unknown'),
        style: { objectFit: 'cover' }
      },

      // Initials fallback — visible only when AvatarImg is hidden.
      // _personLabel collapses to initials via the JSX-side helper
      // below, capped at 2 chars.
      Initials: {
        tag: 'span',
        hide: (el, s) => {
          const v = s?.avatar
          return typeof v === 'string' && /^(https?:|\/)/.test(v)
        },
        text: (el, s) => {
          const label = _personLabel(s) || el.call('polyglot', 'workspace.person.unknown')
          const parts = String(label).trim().split(/\s+/).filter(Boolean)
          if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
          return String(label).slice(0, 2).toUpperCase()
        }
      },

      PcOnlineDot: {
        // O(1) lookup on the canonical per-id presenceById map (populated by
        // syncPresenceById) instead of an O(n) .some() scan of the broad
        // onlineUsers array per card — same "is this email present" semantics.
        if: (el, s) => !!(s.root?.presenceById || {})[String(s.email || '').toLowerCase()],
        position: 'absolute',
        bottom: '0',
        right: '0',
        width: 'Z',
        height: 'Z',
        round: '50%',
        background: 'green',
        // KEPT — punched-out presence-dot ring. Retokenized `'2px'` →
        // `hairline2`.
        borderWidth: 'hairline2',
        borderStyle: 'solid',
        borderColor: 'overlay'
      }
    },

    PcName: {
      flow: 'y',
      gap: 'Y',
      flex: '1',
      // Title falls back through name → email username → 'Unknown'.
      // Previously `text: (el, s) => s.name` rendered raw UUID strings
      // for partially-hydrated rows (no `name`, only `userId`/`_id`).
      // Same chain in PcMail/PcPosition below — never expose raw IDs.
      PcTitle: {
        tag: 'span',
        fontSize: 'B',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => _personLabel(s) || el.call('polyglot', 'workspace.person.unknown')
      },
      PcPosition: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        hide: (el, s) => !s.position,
        text: (el, s) => s.position || ''
      }
    }
  },

  PcDept: {
    tag: 'span',
    alignSelf: 'flex-start',
    padding: 'Y A',
    round: 'radiusCard',
    fontSize: 'Y2',
    fontWeight: '700',
    color: 'title',
    theme: 'dialog',
    border: 'none',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    text: (el, s) => {
      // When the People page groups by team, `groupFilteredPeople`
      // stamps each card copy with `_groupTeamName` — the team of the
      // SECTION it's listed under. A multi-team member appears in
      // several sections, so the badge MUST reflect the section, not
      // the member's arbitrary primary team (which mislabelled e.g.
      // someone under "Class B" as "Class C"). `null` = the "No team"
      // section → "Member"; `undefined` = a non-team lens / other
      // consumer → fall back to the primary real team below.
      if (s._groupTeamName !== undefined) {
        const g = s._groupTeamName
        return g && !_idLike(g) ? g : el.call('polyglot', 'workspace.person.member')
      }
      // Org-scoped + deduped (teams.js personTeams), REAL teams only —
      // system permission groups (Admin / Members / …) are not teams;
      // without the filter every member's badge read "MEMBERS".
      const teams = personTeams({ userId: s.userId, email: s.email }, s.root).filter(
        (t) => !t.isSystem
      )
      const name = teams[0]?.name
      return name && !_idLike(name) ? name : el.call('polyglot', 'workspace.person.member')
    }
  },

  PcMeta: {
    flow: 'y',
    gap: 'Z',
    fontSize: 'Z2',
    color: 'paragraph',
    PcLoc: { tag: 'span', text: (el, s) => `${s.location || '—'}` },
    PcTz: { tag: 'span', color: 'caption', text: (el, s) => s.timezone || '' },
    PcMail: { tag: 'span', color: 'caption', text: (el, s) => s.email }
  },

  // Read-only role badge. Editing happens in the dedicated Role manager
  // panel on the People page (superadmin only).
  PcRole: {
    hide: (el) => !el.call('isSuperAdmin'),
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    paddingTop: 'A',

    PcRoleLabel: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      text: '{{ workspace.person.role | polyglot }}'
    },

    // Role hue survives as a DOT, ink stays neutral — the RmDot pattern from
    // RoleManagerRow below. White ink on the server-driven role hue failed AA
    // for most hues (2.9:1 on green), and the hue set is user-configured so
    // no static ink pairing can be proven safe.
    PcRoleBadge: {
      tag: 'span',
      flow: 'x',
      align: 'center',
      gap: 'Y',
      padding: 'Y A',
      round: 'radiusPill',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'title',
      background: 'overlayStrong',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      PcRoleDot: {
        tag: 'span',
        width: 'Y',
        height: 'Y',
        round: '50%',
        flex: '0 0 auto',
        background: (el, s) => roleColor(s, roleOf(s, s.userId) || 'member')
      },
      PcRoleText: {
        tag: 'span',
        // A system role resolves through the bundle; a tenant role keeps the
        // label its admin wrote (functions/roles.js roleLabelKey).
        text: (el, s) => {
          const role = roleOf(s, s.userId) || 'member'
          const key = roleLabelKey(role)
          return key ? el.call('polyglot', key) : roleLabel(s, role)
        }
      }
    }
  }
}

// Per-row role editor used inside the Role manager panel on the People
// page. Renders a colored role chip + name on the left and a `<select>`
// on the right. A change asks first (functions/roles.js
// confirmPersonRoleChange), which PATCHes the person's OrgMember role via
// sdk.updateOrgMember(orgId, memberId, { role }) only on "Change role".
export const RoleManagerRow = {
  flow: 'x',
  align: 'center space-between',
  gap: 'B',
  padding: 'Z A',
  round: 'radiusSheet',
  ':hover': { theme: 'dialog' },

  RmLeft: {
    flow: 'x',
    align: 'center',
    gap: 'A',
    flex: '1',
    minWidth: '0',
    RmDot: {
      width: 'Z',
      height: 'Z',
      round: '50%',
      flex: '0 0 auto',
      background: (el, s) => roleColor(s, roleOf(s, s.userId) || 'member')
    },
    RmName: {
      flow: 'y',
      gap: 'Y',
      flex: '1',
      minWidth: '0',
      RmNameTitle: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => s.name
      },
      RmNameSub: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        text: (el, s) => s.email
      }
    }
  },

  RmSelect: {
    tag: 'select',
    minHeight: 'controlH',
    padding: '0 A',
    round: 'radiusControl',
    theme: 'dialog',
    border: 'none',
    color: 'title',
    fontSize: 'Z2',
    fontWeight: '700',
    cursor: 'pointer',
    // Your own row: the server refuses an own-role write, so the select is
    // off and says why (CRUD standard rule 6).
    disabled: (el, s) => el.call('isOwnPersonRow', s),
    title: (el, s) =>
      el.call('isOwnPersonRow', s) ? el.call('polyglot', 'workspace.person.roleChange.self') : null,
    // Asks first. The select shows the stored role again on Cancel, on the
    // own-row refusal and on a failed write, and the new one once it is stored.
    onChange: (ev, el, s) => {
      el.call('confirmPersonRoleChange', s, ev.target.value, (role) => el.update({ value: role }))
    },
    onRender: (el, s) => {
      if (!el.node) return
      const fill = () => {
        el.node.innerHTML = ''
        const current = roleOf(s, s.userId) || 'member'
        const roles = allRoles(s)
        const list = roles.length
          ? roles
          : [{ name: 'member', label: el.call('polyglot', 'workspace.person.member') }]
        for (const r of list) {
          const opt = document.createElement('option')
          opt.value = r.name
          opt.textContent = r.label || r.name
          if (r.name === current) opt.selected = true
          el.node.appendChild(opt)
        }
      }
      fill()
      // The roles fetch may resolve after first render. Re-fill once
      // more when the data lands.
      let tries = 0
      const t = setInterval(() => {
        if (allRoles(s).length) {
          fill()
          clearInterval(t)
          return
        }
        if (++tries > 10) clearInterval(t)
      }, 200)
      el._rmTimer = t
    },
    onRemove: (el) => {
      if (el._rmTimer) clearInterval(el._rmTimer)
    }
  }
}

export const DocumentRow = {
  extends: 'Link',
  href: (el, s) => s.url || '/notes',
  textDecoration: 'none',
  color: 'inherit',
  flow: 'x',
  align: 'center',
  gap: 'B',
  padding: 'B 0',
  // Row-separator-as-stroke, banned by design-direction.md §8 / DESIGN_
  // LANGUAGE §2.5 — same fix as the house SettingRow.js; the `:hover`
  // theme fill already carries the row-boundary cue.
  cursor: 'pointer',
  ':hover': { theme: 'dialog' },

  DrIcon: {
    extends: 'Icon',
    icon: 'document',
    boxSize: 'C',
    color: 'blue'
  },

  DrBody: {
    flow: 'y',
    gap: 'Y',
    flex: '1',
    minWidth: '0',
    DrTitle: {
      tag: 'span',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => s.title
    },
    DrMeta: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      text: (el, s) => {
        const parts = []
        if (s.type) parts.push(s.type)
        if (s.size) parts.push(s.size)
        if (s.signedAt) parts.push(`signed ${s.signedAt}`)
        return parts.join(' · ')
      }
    }
  },

  DrAction: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '700',
    color: 'blue',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    text: '{{ workspace.person.open | polyglot }}'
  }
}

// Sidebar filter row on the People page. State carries `kind` ('group' |
// 'role'), `name` (the value to filter by, null = clear), `label`, and
// optional `count`. Click toggles the matching root-state filter.
// Resolves which `root.selected*` field corresponds to a given filter kind.
const _selField = (kind) => {
  if (kind === 'role') return 'selectedRole'
  if (kind === 'category') return 'selectedTeamCategory'
  return 'selectedGroup'
}

export const PeopleFilterLink = {
  tag: 'button',
  flow: 'x',
  align: 'center space-between',
  gap: 'A',
  padding: 'Z A',
  round: 'radiusPill',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  background: (el, s) => (s.root[_selField(s.kind)] === s.name ? 'blue.2' : 'transparent'),
  color: (el, s) => (s.root[_selField(s.kind)] === s.name ? 'title' : 'caption'),
  fontWeight: (el, s) => (s.root[_selField(s.kind)] === s.name ? '700' : '500'),
  ':hover': { color: 'title' },
  onClick: (ev, el, s) => {
    ev.preventDefault()
    s.root.update({ [_selField(s.kind)]: s.name })
  },
  PflLabel: {
    tag: 'span',
    fontSize: 'Z2',
    // KEY-IN-STATE: the rail rows carry a bundle key (peopleGroups.js), a real
    // TEAM row carries the team's own name — polyglot returns a non-key
    // verbatim, so both shapes render correctly through one call.
    text: (el, s) =>
      s.label && !_idLike(s.label)
        ? el.call('polyglot', s.label)
        : el.call('polyglot', 'workspace.people.filter.other')
  },
  PflCount: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => (s.count != null ? String(s.count) : '')
  }
}

// (PeopleTeamCard removed 2026-06-06 — the body Teams card grid moved
// into the left rail as PeopleFilterLink rows fed by
// peopleTeamsDirectory; see pages/people.js PpTeamsRail.)

export const CompanyInfoRow = {
  flow: 'y',
  gap: 'Z',
  // Row-separator-as-stroke, banned by design-direction.md §8 / DESIGN_
  // LANGUAGE §2.5 — same fix as the house SettingRow.js (each row's own
  // padding already gives adjacent rows a full whitespace gap).
  padding: 'B 0',
  CiLabel: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '700',
    color: 'caption',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    text: (el, s) => s.label || s.key
  },
  CiValue: {
    tag: 'span',
    fontSize: 'B',
    color: 'title',
    lineHeight: '1.55',
    text: (el, s) => s.value
  }
}

// THE roster grid track. The member list, the pending invites and the loading
// skeleton sit on one page and must break to the same column count, so the
// track has ONE declaration and the three grids extend it.
//
// Rule 27 — the track is a design-system value, not a raw px. The spacing
// scale is base 17 ratio phi. F3 (260.5px) gave four 256px cards at 1440 and
// name + bio wrapped to four lines (dev, 2026-10-01); the track is G1
// (design-d2). `var(--spacing-G1)` is the form that survives INSIDE
// minmax(): the grid-track resolver lifts bare spacing letters at the TOP
// level only (smbls css-in-props props/grid.js). `min(100%, …)` is the house
// pairing and also stops the track
// overflowing a pane narrower than one card.
export const PeopleGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, var(--spacing-G1)), 1fr))',
  gap: 'B'
}

export const PeopleGroupBlock = {
  flow: 'y',
  gap: 'B',

  PgHead: {
    flow: 'x',
    align: 'baseline',
    gap: 'A',
    // Borderless law — the bold H2 title + the extra `paddingBottom`
    // already separate this header from the grid below.
    paddingBottom: 'Z',

    PgTitle: {
      tag: 'h2',
      fontSize: 'C',
      fontWeight: '700',
      color: 'title',
      letterSpacing: '-0.01em',
      // A group header is either a bundle KEY (the category / no-team buckets)
      // or a real team name — polyglot returns a non-key verbatim.
      text: (el, s) =>
        s.name && !_idLike(s.name)
          ? el.call('polyglot', s.name)
          : el.call('polyglot', 'workspace.people.filter.other')
    },
    PgCount: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      text: (el, s) => `${(s.people || []).length}`
    }
  },

  PgGrid: {
    extends: 'PeopleGrid',
    childExtends: 'PersonCard',
    childrenAs: 'state',
    children: (el, s) => s.people || []
  }
}

// A pending WorkspaceInvitation row on the People page (2026-07-22 — Simone
// AI-assistant invites reported "success" but never showed up anywhere in
// the team UI, so invited-but-not-yet-accepted rows now render here). Not a
// `Link` — there's no profile to visit until the invite is accepted — but
// keeps PersonCard's card shell (padding/round/background) so the
// section reads as part of the same roster grid.
export const PendingInviteCard = {
  flow: 'y',
  gap: 'B',
  padding: 'B',
  round: 'radiusSheet',
  // Fill only — matches PersonCard's card-shell fix above (the hairline was
  // redundant against this card's own `overlay` fill).
  background: 'overlay',

  // Stacked like PersonCard (avatar row on top, text below) — the roster grid
  // columns are ~230px, so a single avatar+email+badge row overflowed: the
  // email box collapsed to ~33px and the text ran under the badge. Top row is
  // avatar ⟷ badge (space-between); the email truncates with a native title
  // tooltip carrying the full address.
  PicHeader: {
    flow: 'y',
    gap: 'A',

    PicTop: {
      flow: 'x',
      align: 'center space-between',
      gap: 'A',

      PicAvatar: {
        align: 'center center',
        width: 'D',
        height: 'D',
        round: '50%',
        color: 'caption',
        background: 'overlayStrong',
        flex: '0 0 auto',
        Icon: { name: 'email', boxSize: 'A' }
      },

      // "Pending" badge — caption text on an overlay-strong chip (house
      // neutral-status pattern, matches pages/system.js STATUS.pending).
      PicBadge: {
        tag: 'span',
        padding: 'Y A',
        round: 'radiusCard',
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'caption',
        background: 'overlayStrong',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        flex: '0 0 auto',
        text: '{{ workspace.person.pending | polyglot }}'
      }
    },

    PicName: {
      flow: 'y',
      gap: 'Y',
      minWidth: '0',
      PicEmail: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        title: (el, s) => s.email,
        text: (el, s) => s.email
      },
      PicRole: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        whiteSpace: 'nowrap',
        // `{role}` is the server's own role id, capitalised by
        // functions/pendingInvitations.js `_label` — a machine identifier the
        // invite API matches on, not copy. Only the FRAME is translated.
        text: (el, s) =>
          el.call('polyglot', 'workspace.person.invitedAs').replace('{role}', s.roleLabel || '')
      }
    }
  },

  // Revoke affordance — admin/owner only (the server 403s the revoke
  // endpoint for anyone else, same gate as loadPendingInvitations).
  PicFooter: {
    hide: (el) => !el.call('currentUserIsAdmin'),
    flow: 'x',
    align: 'center flex-end',

    PicRevoke: {
      tag: 'button',
      type: 'button',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      minHeight: 'controlHCompact',
      padding: '0 Z',
      round: 'radiusPill',
      fontSize: 'Z',
      fontWeight: '700',
      color: 'red',
      ':hover': { theme: 'dialog' },
      text: '{{ workspace.person.revoke | polyglot }}',
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        // Capture PLAIN values before the async confirm: `s` is the keyed
        // child-state proxy, and the optimistic list update after the revoke
        // re-binds it to whichever row shifts into this slot — reading
        // s.email inside onConfirm named the WRONG invite in the success
        // toast (observed live: revoked iva@…, toast said bacho@…).
        const email = s.email
        const inviteId = s.id
        const root = s.root
        // The D1 house shape. In Georgian the Revoke button and Cancel share
        // the word გაუქმება, so the other button says "Keep" (D13).
        el.call('confirmDialog', {
          title: el.call('polyglot', 'workspace.person.revokeConfirm'),
          message: el
            .call('polyglot', 'workspace.person.revokeConfirmBody')
            .replace('{email}', () => email),
          confirmText: el.call('polyglot', 'workspace.person.revokeCta'),
          cancelText: el.call('polyglot', 'shell.invite.link.keep'),
          confirmTheme: 'primary',
          icon: null,
          onConfirm: async () => {
            try {
              await el.call('revokePendingInvitation', root, inviteId)
              el.call('openNotification', {
                title: el.call('polyglot', 'workspace.person.revoked'),
                message: el
                  .call('polyglot', 'workspace.person.revokedBody')
                  .replace('{email}', email),
                type: 'success'
              })
            } catch (err) {
              el.call('openNotification', {
                title: el.call('polyglot', 'workspace.person.revokeFailed'),
                message: String(err && err.message ? err.message : err),
                type: 'error'
              })
              console.warn('[PendingInviteCard] revoke failed:', err)
            }
          }
        })
      }
    }
  }
}

// Pending-invitations section for the People page body — same PgHead +
// grid shape as PeopleGroupBlock so it reads as a natural section above
// the team groups. Hidden entirely when there's nothing pending, or when
// the viewer can't manage invites (see PendingInviteCard header comment —
// the server 403s a non-admin's list call, so this gate keeps the section
// from firing a request that will always fail).
export const PendingInvitesBlock = {
  flow: 'y',
  gap: 'B',
  hide: (el, s) => !el.call('currentUserIsAdmin') || !(s.root?.pendingInvitations || []).length,
  // Direct URL loads mount this block BEFORE the workspace is hydrated, and
  // loadPendingInvitations no-ops (clears the list) without an active
  // workspace id — onInit alone left the section permanently empty on a hard
  // load. Subscribe to the workspace key and re-fire the (deduped,
  // TTL-cached) loader when it lands or changes — the same subscribeTo +
  // onUpdate pattern as TicketsKanban's _colCount.
  subscribeTo: ['workspace'],
  onInit: (el, s) => {
    if (el.call('currentUserIsAdmin')) el.call('loadPendingInvitations', s.root)
  },
  onUpdate: (el, s) => {
    if (el.call('currentUserIsAdmin')) el.call('loadPendingInvitations', s.root)
  },

  PgHead: {
    flow: 'x',
    align: 'baseline',
    gap: 'A',
    // Borderless law — the bold H2 title + the extra `paddingBottom`
    // already separate this header from the grid below.
    paddingBottom: 'Z',

    PgTitle: {
      tag: 'h2',
      fontSize: 'C',
      fontWeight: '700',
      color: 'title',
      letterSpacing: '-0.01em',
      text: '{{ workspace.person.pendingInvites | polyglot }}'
    },
    PgCount: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      text: (el, s) => `${(s.root?.pendingInvitations || []).length}`
    }
  },

  PgGrid: {
    extends: 'PeopleGrid',
    childExtends: 'PendingInviteCard',
    childrenAs: 'state',
    children: (el, s) => s.root?.pendingInvitations || []
  }
}
